// Floating customer service widget. Markup: snippets/customer-service.liquid
//
// The form posts to Shopify's native `contact` endpoint, so messages reach the store's customer
// service inbox with or without JavaScript.
//
// It is deliberately NOT posted with fetch. Shopify rejects a contact submission that carries no
// CAPTCHA token ("400 Missing CAPTCHA token"), and that token is attached by Shopify's own script
// while it handles a native submit — calling preventDefault() skips the token and the post always
// fails. So the native submit is kept and simply aimed at a hidden frame, which leaves the page in
// place. Should the response land in the top window anyway, the confirmation is restored after the
// reload from a sessionStorage flag.

(function initCustomerService() {
  const widget = document.querySelector('[data-customer-service]');
  if (!widget) return;

  const launcher = widget.querySelector('[data-cs-launcher]');
  const panel = widget.querySelector('[data-cs-panel]');
  const closeButton = widget.querySelector('[data-cs-close]');
  const form = widget.querySelector('form');
  const thanksTemplate = widget.querySelector('[data-cs-thanks]');
  if (!launcher || !panel) return;

  const PENDING_KEY = 'customer-service:pending';

  const readPending = () => {
    try {
      return sessionStorage.getItem(PENDING_KEY) === '1';
    } catch {
      return false;
    }
  };

  const writePending = (value) => {
    try {
      if (value) sessionStorage.setItem(PENDING_KEY, '1');
      else sessionStorage.removeItem(PENDING_KEY);
    } catch {
      // Private browsing can refuse storage; the confirmation is best-effort in that case.
    }
  };

  const setOpen = (open) => {
    widget.dataset.open = String(open);
    launcher.setAttribute('aria-expanded', String(open));
    panel.inert = !open;

    if (open) {
      const firstField = panel.querySelector('input:not([type="hidden"]), textarea');
      // Wait for the panel to become visible, otherwise focus() is a no-op.
      requestAnimationFrame(() => firstField?.focus());
    } else {
      launcher.focus();
    }
  };

  // Replaces the form with the confirmation, so the panel reads like a sent message.
  const showThanks = () => {
    const confirmation = thanksTemplate?.content.firstElementChild?.cloneNode(true);
    if (!confirmation || !form?.isConnected) return;
    form.replaceWith(confirmation);
    confirmation.focus();
  };

  launcher.addEventListener('click', () => setOpen(widget.dataset.open !== 'true'));
  closeButton?.addEventListener('click', () => setOpen(false));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && widget.dataset.open === 'true') setOpen(false);
  });

  // Restore the outcome of a submit that navigated the page — either a no-JS submit, or the frame
  // target being ignored. Shopify appends `contact_posted` once a message is recorded.
  const serverConfirmation = panel.querySelector('[data-cs-success]');
  const serverError = panel.querySelector('[data-cs-error]');
  const justPosted = new URLSearchParams(window.location.search).has('contact_posted');
  const wasPending = readPending();

  if (serverConfirmation || serverError || (justPosted && wasPending)) {
    widget.dataset.open = 'true';
    launcher.setAttribute('aria-expanded', 'true');
    panel.inert = false;
    // The server only renders its confirmation when it can tie the post back to this form; when it
    // cannot, the flag plus `contact_posted` is enough to know the message went through.
    if (!serverConfirmation && !serverError) showThanks();
  } else {
    panel.inert = true;
  }

  if (justPosted || wasPending) writePending(false);

  const frame = widget.querySelector('[data-cs-frame]');
  const frameName = frame?.getAttribute('name');
  // Without a frame the form submits normally and the block above renders the outcome.
  if (!form || !frame || !frameName) return;

  const submitButton = form.querySelector('[data-cs-submit]');
  const statusRegion = form.querySelector('[data-cs-status]');
  const sendingLabel = widget.dataset.sendingLabel || '';
  const idleLabel = submitButton?.textContent?.trim() || '';

  form.target = frameName;

  let awaitingResponse = false;
  let timeoutId = 0;

  const finishSending = () => {
    awaitingResponse = false;
    window.clearTimeout(timeoutId);
    writePending(false);
    if (submitButton) {
      submitButton.disabled = false;
      if (idleLabel) submitButton.textContent = idleLabel;
    }
  };

  form.addEventListener('submit', () => {
    // No preventDefault here on purpose — Shopify's handler has to run to attach the CAPTCHA token.
    awaitingResponse = true;
    writePending(true);
    if (statusRegion) statusRegion.textContent = '';
    if (submitButton) {
      submitButton.disabled = true;
      if (sendingLabel) submitButton.textContent = sendingLabel;
    }
    // Release the form again if the response never arrives, so the customer can retry.
    timeoutId = window.setTimeout(finishSending, 20000);
  });

  frame.addEventListener('load', () => {
    if (!awaitingResponse) return; // The frame also fires load for its initial empty document.

    let doc = null;
    try {
      doc = frame.contentDocument;
    } catch {
      // A cross-origin response cannot be inspected; treat the completed post as delivered.
    }

    const error = doc?.querySelector('[data-cs-error]') ?? null;
    const isStorefrontPage = !!doc?.querySelector('[data-customer-service]');

    finishSending();

    if (error) {
      if (statusRegion) statusRegion.textContent = error.textContent.trim();
      return;
    }

    // A readable response that is not a storefront page means Shopify answered with something the
    // customer has to deal with, such as a CAPTCHA challenge. Show it rather than claim success.
    if (doc && !isStorefrontPage) {
      const url = frame.contentWindow?.location?.href;
      if (url) window.location.assign(url);
      return;
    }

    showThanks();
  });
})();
